import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  Menu, MenuItem, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid, Avatar,
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon, Edit as EditIcon, Add as AddIcon,
  MiscellaneousServices as ServicosIcon, PriorityHigh as PriorityIcon,
} from '@mui/icons-material';
import wagService, { WAGServico } from '../../services/wag.service';

const WAGServicos: React.FC = () => {
  const [items, setItems] = useState<WAGServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<WAGServico | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<Partial<WAGServico>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await wagService.getServicos());
    } catch {
      setError('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !search || i.desig.toLowerCase().includes(search.toLowerCase()) || i.sigla.toLowerCase().includes(search.toLowerCase())
  );

  async function handleCreate() {
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.createServico(form);
      setCreateOpen(false);
      setForm({});
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao criar');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit() {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.updateServico(selected.id, form);
      setEditOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function toggleEstado() {
    if (!selected) return;
    await wagService.updateServico(selected.id, { estado: selected.estado === 'A' ? 'I' : 'A' });
    setAnchorEl(null);
    load();
  }

  function openCreate() { setForm({ tempoMedio: 10, prioridade: 'N' }); setSaveError(null); setCreateOpen(true); }
  function openEdit(s: WAGServico) {
    setSelected(s);
    setForm({ desig: s.desig, sigla: s.sigla, descr: s.descr, tempoMedio: s.tempoMedio, prioridade: s.prioridade });
    setSaveError(null);
    setEditOpen(true);
    setAnchorEl(null);
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Serviços WAG</Typography>
          <Typography variant="body2" color="text.secondary">Tipos de atendimento disponíveis no quiosque</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
          Novo Serviço
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth placeholder="Pesquisar por designação ou sigla…" value={search}
          onChange={e => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Serviço</TableCell>
              <TableCell>Sigla</TableCell>
              <TableCell>Tempo médio</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum serviço encontrado</TableCell></TableRow>
            ) : filtered.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    {s.urlLogo
                      ? <Avatar src={s.urlLogo} sx={{ width: 36, height: 36 }} />
                      : <Avatar sx={{ width: 36, height: 36, bgcolor: '#e94560', fontSize: 14, fontWeight: 700 }}>{s.sigla}</Avatar>
                    }
                    <Box>
                      <Typography fontWeight={600}>{s.desig}</Typography>
                      {s.descr && <Typography variant="caption" color="text.secondary">{s.descr}</Typography>}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Chip label={s.sigla} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} /></TableCell>
                <TableCell>{s.tempoMedio} min</TableCell>
                <TableCell>
                  {s.prioridade === 'S'
                    ? <Chip icon={<PriorityIcon />} label="Permite" size="small" color="warning" />
                    : <Typography variant="caption" color="text.secondary">—</Typography>
                  }
                </TableCell>
                <TableCell>
                  <Chip label={s.estado === 'A' ? 'Activo' : 'Inactivo'} size="small"
                    color={s.estado === 'A' ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={e => { setSelected(s); setAnchorEl(e.currentTarget); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => selected && openEdit(selected)}><EditIcon fontSize="small" sx={{ mr: 1 }} />Editar</MenuItem>
        <MenuItem onClick={toggleEstado} sx={{ color: selected?.estado === 'A' ? 'error.main' : 'success.main' }}>
          {selected?.estado === 'A' ? 'Desactivar' : 'Activar'}
        </MenuItem>
      </Menu>

      {/* Dialog Criar / Editar (reutilizado) */}
      {[{ open: createOpen, title: 'Novo Serviço', onClose: () => setCreateOpen(false), onSave: handleCreate },
        { open: editOpen, title: `Editar — ${selected?.desig}`, onClose: () => setEditOpen(false), onSave: handleEdit },
      ].map(({ open, title, onClose, onSave }) => (
        <Dialog key={title} open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={8}>
                <TextField label="Designação *" fullWidth size="small" value={form.desig ?? ''}
                  onChange={e => setForm(p => ({ ...p, desig: e.target.value }))} />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Sigla *" fullWidth size="small" value={form.sigla ?? ''}
                  onChange={e => setForm(p => ({ ...p, sigla: e.target.value.toUpperCase().slice(0, 5) }))}
                  helperText="Máx. 5 car." />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Código *" fullWidth size="small" value={form.codigo ?? ''}
                  onChange={e => setForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                  disabled={open === editOpen} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Tempo médio (min)" type="number" fullWidth size="small"
                  value={form.tempoMedio ?? 10}
                  onChange={e => setForm(p => ({ ...p, tempoMedio: parseInt(e.target.value) || 10 }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Descrição" fullWidth size="small" multiline rows={2}
                  value={form.descr ?? ''}
                  onChange={e => setForm(p => ({ ...p, descr: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <input type="checkbox" id="prioridade" checked={form.prioridade === 'S'}
                    onChange={e => setForm(p => ({ ...p, prioridade: e.target.checked ? 'S' : 'N' }))} />
                  <label htmlFor="prioridade">
                    <Typography variant="body2">Permitir senhas prioritárias neste serviço</Typography>
                  </label>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button variant="contained" onClick={onSave} disabled={saving || !form.desig || !form.sigla}
              sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
              {saving ? 'A guardar…' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>
      ))}
    </Box>
  );
};

export default WAGServicos;
